import React, { useState } from "react";
import { Alert, Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "../../lib/api";

export interface LearnedCommission {
  basis: "percentage" | "flat";
  value: number;
  source_invoice_id: string;
  recorded_at: string;
}

export interface PropertyInvoiceHistory {
  learned_commission: LearnedCommission | null;
  invoices: {
    id: string;
    invoice_number: string;
    amount: number;
    status: string;
    move_in_date: string;
  }[];
  move_ins: {
    client_id: string;
    client_name?: string;
    move_in_date: string;
    source_invoice_id: string;
  }[];
}

interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string;
  title?: string;
}

interface Activity {
  id: string;
  status: string;
  occurred_at: string;
  client_id: string | null;
}

const clientLink = (id: string) => `?page=clients&client=${encodeURIComponent(id)}`;

export function PropertyMoney({ propertyId }: { propertyId: string }) {
  const history = useQuery({
    queryKey: ["property-invoice-history", propertyId],
    queryFn: () => fetchWithAuth<PropertyInvoiceHistory>(`/properties-management/${propertyId}/invoice-history`),
  });
  if (history.isLoading) return <Typography>Loading invoice history…</Typography>;
  if (history.error) {
    return <Alert severity="warning" action={<Button onClick={() => history.refetch()}>Retry</Button>}>Couldn't load invoice history.</Alert>;
  }
  const commission = history.data?.learned_commission;
  return (
    <Stack spacing={2} sx={{ "& .MuiTypography-body1, & .MuiInputBase-root": { fontSize: 14 }, fontVariantNumeric: "tabular-nums" }}>
            <Typography>
        {commission
          ? `Pays commission · ${commission.basis === "flat" ? `$${commission.value} flat fee` : `${commission.value}% of first month's rent`} · learned from invoice`
          : "No commission recorded on an invoice yet."}
      </Typography>
      {commission && <Typography variant="caption">Recorded {new Date(commission.recorded_at).toLocaleDateString()} · source invoice {commission.source_invoice_id}</Typography>}
      {history.data?.invoices.map(invoice => (
        <Typography key={invoice.id}>{invoice.invoice_number || invoice.id} · ${Number(invoice.amount).toFixed(2)} · {invoice.status}</Typography>
      ))}
      {!history.data?.invoices.length && <Typography>No invoices yet.</Typography>}
      <Typography variant="h6">Clients moved in here</Typography>
      <Typography variant="caption">Recorded on sent or paid invoices with a past move-in date. Visible to your account.</Typography>
      {history.data?.move_ins.map(moveIn => (
        <Box key={`${moveIn.client_id}:${moveIn.move_in_date}`}>
          <Link href={clientLink(moveIn.client_id)}>{moveIn.client_name || "Open client"}</Link> · {moveIn.move_in_date}
          <Typography variant="caption" display="block">Source invoice {moveIn.source_invoice_id}</Typography>
        </Box>
      ))}
      {!history.data?.move_ins.length && <Typography>No recorded move-ins yet.</Typography>}
    </Stack>
  );
}

export function PropertyContacts({ propertyId, canEdit }: { propertyId: string; canEdit: boolean }) {
  const contacts = useQuery({
    queryKey: ["property-contacts", propertyId],
    queryFn: () => fetchWithAuth<{ contacts: Contact[] }>(`/properties-management/${propertyId}/contacts`),
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", title: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const labels = { name: "Name", email: "Email", phone: "Phone", title: "Role / manager" };

  async function save() {
    setSaving(true);
    setError("");
    try {
      await fetchWithAuth(`/properties-management/${propertyId}/contacts`, { method: "POST", body: JSON.stringify(form) });
      setForm({ name: "", email: "", phone: "", title: "" });
      await contacts.refetch();
    } catch {
      setError("Could not confirm contact save. Refresh the list before trying again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing={2} sx={{ "& .MuiTypography-body1, & .MuiInputBase-root": { fontSize: 14 }, fontVariantNumeric: "tabular-nums" }}>
      <Typography variant="h6">Leasing contacts</Typography>
      {contacts.error ? (
        <Alert action={<Button onClick={() => contacts.refetch()}>Retry</Button>}>Couldn't load contacts.</Alert>
      ) : contacts.isLoading ? <Typography>Loading contacts…</Typography>
        : !contacts.data?.contacts.length ? <Typography>No contacts saved yet.</Typography>
          : contacts.data.contacts.map(contact => (
            <Box key={contact.id}>
              <Typography>{contact.name} · {contact.title || "Leasing"}</Typography>
              <Link href={`mailto:${contact.email}`}>{contact.email}</Link>
              {contact.phone && <><br /><Link href={`tel:${contact.phone}`}>{contact.phone}</Link></>}
            </Box>
          ))}
      {canEdit && (
        <Box component="form" onSubmit={event => { event.preventDefault(); void save(); }}>
          <Stack spacing={1} sx={{ "& .MuiTypography-body1": { fontSize: 14 } }}>
            {(Object.keys(form) as (keyof typeof form)[]).map(key => (
              <TextField key={key} size="small" label={labels[key]} required={key === "name" || key === "email"}
                type={key === "email" ? "email" : "text"} value={form[key]}
                onChange={event => setForm({ ...form, [key]: event.target.value })} />
            ))}
            {error && <Alert severity="error" action={<Button onClick={() => contacts.refetch()}>Refresh list</Button>}>{error}</Alert>}
            <Button type="submit" disabled={saving}>Save contact</Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export function PropertyActivity({ propertyId }: { propertyId: string }) {
  const activity = useQuery({
    queryKey: ["property-activity", propertyId],
    queryFn: () => fetchWithAuth<Activity[]>(`/properties-management/${propertyId}/activity`),
  });
  return (
    <Stack spacing={1} sx={{ "& .MuiTypography-body1": { fontSize: 14 } }}>
            <Typography variant="caption">Existing shortlist events visible to your account; invoice move-ins appear under Money.</Typography>
      {activity.isLoading ? <Typography>Loading activity…</Typography>
        : activity.error ? <Alert action={<Button onClick={() => activity.refetch()}>Retry</Button>}>Couldn't load activity.</Alert>
          : !activity.data?.length ? <Typography>No recorded activity yet.</Typography>
            : activity.data.map(event => (
              <Typography key={event.id}>
                {event.status.replace(/_/g, " ")} · {new Date(event.occurred_at).toLocaleDateString()}{" "}
                {event.client_id && <Link href={clientLink(event.client_id)}>Open client</Link>}
              </Typography>
            ))}
    </Stack>
  );
}
